import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Settings2, FileDown, Pencil, Trash2, RefreshCw, CalendarCheck, Sparkles, Utensils, List, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { BookingFormDialog } from "@/components/app/BookingFormDialog";
import { BookingConfigDialog } from "@/components/app/BookingConfigDialog";
import { BookingItineraryDialog } from "@/components/app/BookingItineraryDialog";
import { BookingMenuDialog } from "@/components/app/BookingMenuDialog";
import { BookingsCalendar } from "@/components/app/BookingsCalendar";

const STATUS_CLR = {
  tentative: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  checked_in: "bg-sky-50 text-sky-700 border-sky-200",
  checked_out: "bg-zinc-100 text-zinc-600 border-zinc-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const fmt = (s) => { try { return new Date(s).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); } catch { return s || "—"; } };

export default function Bookings() {
  const { hasPermission } = useAuth();
  const { selectedId, select } = useProperty();
  const canConfig = hasPermission("bookings_config");

  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [itinFor, setItinFor] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [view, setView] = useState("list");

  const loadConfig = useCallback(async () => {
    try { const { data } = await api.get("/bookings/config"); setConfig(data); }
    catch (e) { /* permission edge */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedId) params.set("property_id", selectedId);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/bookings?${params.toString()}`);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bookings");
    } finally { setLoading(false); }
  }, [selectedId, statusFilter, search]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { load(); }, [load]);

  // After a booking is created/updated, make sure it's visible. If it belongs
  // to a property other than the one currently being filtered, switch the
  // active property to it (which re-triggers `load`); otherwise just reload.
  const handleSaved = useCallback((saved) => {
    if (saved?.property_id && saved.property_id !== selectedId) {
      if (statusFilter !== "all" && saved.status && saved.status !== statusFilter) {
        setStatusFilter("all");
      }
      select(saved.property_id);
    } else {
      if (statusFilter !== "all" && saved?.status && saved.status !== statusFilter) {
        setStatusFilter("all");
        return; // status change re-triggers load via dependency
      }
      load();
    }
  }, [selectedId, statusFilter, select, load]);

  const labelOf = (field, key) => {
    if (!key) return "—";
    const o = (config?.options?.[field] || []).find((x) => x.key === key);
    return o ? o.label : key;
  };

  const downloadPdf = async (b) => {
    setPdfId(b.id);
    try {
      const resp = await api.get(`/bookings/${b.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `booking_${b.reference || b.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally { setPdfId(null); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    try { await api.delete(`/bookings/${delTarget.id}`); toast.success("Booking deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-overline">Operations</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Bookings</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Capture guest bookings, allocate villas and add-on experiences, and download a branded confirmation PDF.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="bookings-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          {canConfig && <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="bookings-configure"><Settings2 className="w-4 h-4 mr-1" /> Configure</Button>}
          <Button className="bg-zinc-950" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="bookings-new"><Plus className="w-4 h-4 mr-1" /> New booking</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client name…" className="h-9 w-full max-w-xs" data-testid="bookings-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(config?.options?.status || []).map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto inline-flex rounded-md border border-zinc-200 overflow-hidden">
          <button onClick={() => setView("list")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 ${view === "list" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="view-list"><List className="w-4 h-4" /> List</button>
          <button onClick={() => setView("calendar")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 ${view === "calendar" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid="view-calendar"><CalendarDays className="w-4 h-4" /> Calendar</button>
        </div>
      </div>

      {!loading && view === "calendar" && (
        <BookingsCalendar bookings={rows} labelOf={labelOf} onSelect={(b) => { setEditing(b); setFormOpen(true); }} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : view !== "list" ? null : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md py-16 text-center text-zinc-400">
          <CalendarCheck className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
          No bookings yet. Click <strong>New booking</strong> to add one.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="bookings-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                <th className="py-2.5 px-4">Reference</th>
                <th className="py-2.5 px-3">Client</th>
                <th className="py-2.5 px-3">Property</th>
                <th className="py-2.5 px-3">Check-in</th>
                <th className="py-2.5 px-3">Check-out</th>
                <th className="py-2.5 px-3">Pax</th>
                <th className="py-2.5 px-3">Type</th>
                <th className="py-2.5 px-3">Villa</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`booking-row-${b.id}`}>
                  <td className="py-2.5 px-4 font-mono text-xs">{b.reference}</td>
                  <td className="py-2.5 px-3 font-medium">
                    <div className="flex items-center gap-1.5">
                      {b.client_name}
                      {b.itinerary?.status === "ready" && (b.itinerary?.key_pointers?.length > 0) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5" title="AI key pointers" data-testid={`booking-keypoints-${b.id}`}>
                          <Sparkles className="w-3 h-3" /> {b.itinerary.key_pointers.length}
                        </span>
                      )}
                      {b.itinerary?.status === "processing" && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
                      {b.menu?.status === "ready" && (b.menu?.item_count > 0) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-orange-50 text-orange-700 border border-orange-200 rounded px-1.5 py-0.5" title="Food menu items" data-testid={`booking-menucount-${b.id}`}>
                          <Utensils className="w-3 h-3" /> {b.menu.item_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-zinc-600">{b.property_name || "—"}</td>
                  <td className="py-2.5 px-3">{fmt(b.check_in_date)}</td>
                  <td className="py-2.5 px-3">{fmt(b.check_out_date)}</td>
                  <td className="py-2.5 px-3">{b.num_pax ?? "—"}</td>
                  <td className="py-2.5 px-3">{labelOf("booking_type", b.booking_type)}</td>
                  <td className="py-2.5 px-3">{labelOf("villa", b.villa)}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-[10px] font-bold uppercase tracking-widest rounded px-1.5 py-0.5 border ${STATUS_CLR[b.status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{labelOf("status", b.status)}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setItinFor(b)} title="Itinerary & key pointers" data-testid={`booking-itinerary-${b.id}`}><Sparkles className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setMenuFor(b)} title="Food menu" data-testid={`booking-menu-${b.id}`}><Utensils className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(b)} disabled={pdfId === b.id} title="Download PDF" data-testid={`booking-pdf-${b.id}`}>
                        {pdfId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(b); setFormOpen(true); }} title="Edit" data-testid={`booking-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setDelTarget(b)} title="Delete" data-testid={`booking-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BookingFormDialog open={formOpen} onOpenChange={(o) => { if (!o) setEditing(null); setFormOpen(o); }} config={config} editing={editing} onSaved={handleSaved} />
      {canConfig && <BookingConfigDialog open={configOpen} onOpenChange={setConfigOpen} config={config} reload={loadConfig} />}
      <BookingItineraryDialog open={!!itinFor} onOpenChange={(o) => { if (!o) setItinFor(null); }} booking={itinFor} onChanged={load} />
      <BookingMenuDialog open={!!menuFor} onOpenChange={(o) => { if (!o) setMenuFor(null); }} booking={menuFor} onChanged={load} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete booking?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the booking for <strong>{delTarget?.client_name}</strong> ({delTarget?.reference}).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="booking-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
